import { DialogComponent } from '@theme/dialog';

/**
 * The sold-out Notify me overlay - all of DialogComponent's existing behaviour (native <dialog>
 * showModal()/close(), scroll-lock, Escape-to-close, fade transition via CSS), plus one addition:
 * clicking the white overlay itself, outside the email form and a protected buffer around it,
 * closes the overlay. DialogComponent's own built-in outside-click check (assets/dialog.js,
 * isClickedOutside(event, this.refs.dialog)) can never fire here - this dialog IS the whole
 * viewport, so nothing a user clicks is ever "outside" it in that sense. The real "outside" this
 * task means is outside the FORM specifically, which needs its own, separate hit test.
 *
 * Also overrides showDialog() to preventDefault() the triggering event first - DialogComponent's
 * own base implementation (assets/dialog.js) never does, which is fine for a plain <button>
 * trigger (blocks/sold-out-notify.liquid's own Notify me) but not for a real <a href> trigger
 * (the empty-collection state's own "join the waitlist" link, snippets/product-grid.liquid) -
 * without this, clicking it would navigate to its own href (a real page reload) instead of
 * opening the overlay, confirmed live before this fix. A harmless no-op for a button trigger,
 * which has no default navigation to prevent - one override serves both callers correctly
 * instead of needing per-trigger-type logic.
 *
 * @extends DialogComponent
 */
class SoldOutNotifyDialog extends DialogComponent {
  /** Buffer (px) around the protected form's own bounding box that also does not close the
   *  overlay - a near-miss click just outside the visible field/button shouldn't feel like a
   *  mis-tap that closes the whole thing. */
  static #PROTECTED_BUFFER = 50;

  /** @param {Event} [event] */
  showDialog(event) {
    event?.preventDefault?.();
    super.showDialog();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleOverlayClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleOverlayClick);
  }

  /** @param {MouseEvent} event */
  #handleOverlayClick = (event) => {
    const { dialog, protectedZone, closeButton } = this.refs;
    if (!dialog?.open || !protectedZone) return;
    // The close button already closes via its own /closeDialog binding - skip it here so
    // closeDialog() isn't invoked a second time for the same click.
    if (event.target instanceof Element && closeButton?.contains(event.target)) return;

    const rect = protectedZone.getBoundingClientRect();
    const buffer = SoldOutNotifyDialog.#PROTECTED_BUFFER;
    const withinProtectedZone =
      event.clientX >= rect.left - buffer &&
      event.clientX <= rect.right + buffer &&
      event.clientY >= rect.top - buffer &&
      event.clientY <= rect.bottom + buffer;

    if (!withinProtectedZone) {
      this.closeDialog();
    }
  };
}

if (!customElements.get('sold-out-notify-dialog')) {
  customElements.define('sold-out-notify-dialog', SoldOutNotifyDialog);
}
